/**
 * Admin Settings Module Documentation
 */

export const settingsDoc = {
  title: 'Admin Settings',
  content: `# Admin Settings Module Training Guide

## 1. Overview

The **Admin Settings Module** controls system-wide configurations including user access, pricing rules, tax configuration, and email templates.

**Key Concept:** Settings changes affect the entire system. Review carefully before saving.

---

## 2. Settings Organization

\`\`\`mermaid
flowchart TB
    subgraph Settings["⚙️ Admin Settings"]
        direction TB
        RULES[Rules]
        SEC[Security]
        GEN[General]
    end
    
    subgraph RULES_Sub["📋 Rules"]
        PRICE[Pricing]
        DISC[Discounts]
        TAX[Sales Tax]
        REM[Reminders]
    end
    
    subgraph SEC_Sub["🔒 Security"]
        USERS[Users]
        ROLES[Roles]
        AUDIT[Audit Log]
    end
    
    subgraph GEN_Sub["🏢 General"]
        LOGO[Logo]
        EMAIL[Email Templates]
        COMP[Company Info]
        BANK[Bank Accounts]
    end
    
    RULES --> RULES_Sub
    SEC --> SEC_Sub
    GEN --> GEN_Sub
    
    style Settings fill:#8B7355,color:#fff
    style RULES fill:#3b82f6,color:#fff
    style SEC fill:#ef4444,color:#fff
    style GEN fill:#6b7280,color:#fff
\`\`\`

---

## 3. Accessing Admin Settings

**Navigation:** Admin Panel → **Settings**

---

## 4. Settings Categories

| Section | Description |
|---------|-------------|
| **Rules** | Business rules and automation |
| **Pricing** | Pricing configuration |
| **Discounts** | Discount codes |
| **Tax Settings** | Tax rates |
| **Users** | User management |
| **Email Templates** | Notification templates |

---

## 5. User Management

### User Role Hierarchy

\`\`\`mermaid
flowchart TB
    ADMIN[👑 Admin] --> MGR[📊 Manager]
    MGR --> SALES[💼 Sales]
    MGR --> PROD[🔧 Production]
    MGR --> ACCT[💰 Accounting]
    SALES --> VIEW[👁 Viewer]
    PROD --> VIEW
    ACCT --> VIEW
    
    style ADMIN fill:#ef4444,color:#fff
    style MGR fill:#f59e0b,color:#fff
    style SALES fill:#3b82f6,color:#fff
    style PROD fill:#22c55e,color:#fff
    style ACCT fill:#8b5cf6,color:#fff
    style VIEW fill:#e5e7eb
\`\`\`

### User Roles

| Role | Access Level |
|------|--------------|
| **Admin** | Full system access |
| **Manager** | Orders, customers, reports |
| **Sales** | Orders, quotes, customers |
| **Production** | Work orders only |
| **Accounting** | Invoices, payments |
| **Viewer** | Read-only access |

### Creating a User

1. Navigate to **Settings → Users**
2. Click **"+ Add User"**
3. Enter name, email, password
4. Select role
5. Click **"Save"**

### User Fields

| Field | Required | Description |
|-------|----------|-------------|
| **Name** | Yes | Display name |
| **Email** | Yes | Login email |
| **Password** | Yes | Login password |
| **Role** | Yes | Permission level |
| **Active** | Yes | Enable/disable |

---

## 6. Tax Settings

### Tax Configuration Flow

\`\`\`mermaid
flowchart LR
    subgraph Setup["📋 Setup"]
        RATE[Create Tax Rate]
        STATE[Set State/Region]
        PCT[Set Percentage]
    end
    
    subgraph Apply["✅ Apply"]
        ORDER[Order Created]
        CALC[Tax Calculated]
        INV[Invoice Generated]
    end
    
    Setup --> Apply
    
    style RATE fill:#fef08a
    style CALC fill:#86efac
\`\`\`

| Setting | Description |
|---------|-------------|
| **Tax Rates** | State/local rates |
| **Tax Exemptions** | Exempt handling |
| **Tax on Shipping** | Tax shipping? |

### Setting Up Tax Rates

1. Navigate to **Settings → Tax**
2. Click **"+ Add Tax Rate"**
3. Enter State, Rate %, Date
4. Click **"Save"**

---

## 7. Email Templates

### Email Workflow

\`\`\`mermaid
flowchart LR
    TRIGGER[📌 Trigger Event] --> TEMPLATE[📝 Select Template]
    TEMPLATE --> VARS[🔄 Replace Variables]
    VARS --> SEND[📤 Send Email]
    SEND --> LOG[📋 Log Activity]
    
    style TRIGGER fill:#fef08a
    style SEND fill:#86efac
\`\`\`

### Available Templates

| Template | Trigger |
|----------|---------|
| **Order Confirmation** | Order created |
| **Invoice Sent** | Invoice emailed |
| **Payment Receipt** | Payment recorded |
| **Shipping Notification** | Order shipped |
| **Payment Reminder** | Payment due |

### Template Variables

| Variable | Description |
|----------|-------------|
| \`{{customer_name}}\` | Customer's name |
| \`{{order_number}}\` | Order ID |
| \`{{total}}\` | Order total |
| \`{{due_date}}\` | Payment due date |
| \`{{tracking_number}}\` | Shipping tracking |

### Customizing Templates

1. Navigate to **Settings → Email Templates**
2. Select template
3. Modify subject and body
4. Use variables like {{customer_name}}
5. Preview and **Save**

---

## 8. Discount Settings

### Discount Types

| Type | Description |
|------|-------------|
| **Percentage** | X% off |
| **Fixed Amount** | $X off |
| **Volume** | Quantity discount |

### Creating a Discount

1. Navigate to **Settings → Discounts**
2. Click **"+ Add Discount"**
3. Enter code and type
4. Set value and limits
5. Click **"Save"**

---

## 9. Permission Matrix

\`\`\`mermaid
flowchart TB
    subgraph Access["🔑 Access Levels"]
        FULL[Full Access]
        EDIT[Edit]
        VIEW[View Only]
        NONE[No Access]
    end
    
    subgraph Areas["📍 Areas"]
        ORD[Orders]
        CUST[Customers]
        INV[Invoices]
        SET[Settings]
    end
    
    FULL --> ORD
    FULL --> CUST
    FULL --> INV
    FULL --> SET
    
    style FULL fill:#22c55e,color:#fff
    style EDIT fill:#3b82f6,color:#fff
    style VIEW fill:#fef08a
    style NONE fill:#ef4444,color:#fff
\`\`\`

---

## 10. Tips & Best Practices

### Do's ✅

- Test email templates before going live
- Start with minimal user permissions
- Keep tax rates updated
- Backup settings before major changes

### Don'ts ❌

- Don't give Admin access unnecessarily
- Don't modify tax without approval
- Don't delete users - deactivate them
- Don't share login credentials

---

## 11. Security Best Practices

- Use strong passwords (8+ characters)
- Review user access quarterly
- Remove access when staff leave
- Log out when leaving computer

---

*Last Updated: February 2026*
`
};
